import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchPaymentBinding, savePaymentBinding, type PaymentBinding } from '@/api/wallet';
import { getToken } from '@/lib/auth-storage';

const emptyBinding: PaymentBinding = {
  usdtAddress: '',
  bankAccountName: '',
  bankName: '',
  bankCardNo: '',
  bankBranch: '',
};

export default function PaymentBindingPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<PaymentBinding>(emptyBinding);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getToken()) {
      navigate('/login?redirect=/payment-binding');
      return;
    }
    fetchPaymentBinding()
      .then((res) => {
        if (res.code === 0 && res.data) {
          setForm({ ...emptyBinding, ...res.data });
        } else {
          setError(res.msg || '加载失败');
        }
      })
      .catch(() => setError('网络异常，请稍后重试'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const updateField = (key: keyof PaymentBinding, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');
    setError('');
    try {
      const res = await savePaymentBinding(form);
      if (res.code === 0) {
        setMessage('保存成功');
      } else {
        setError(res.msg || '保存失败');
      }
    } catch {
      setError('网络异常，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-5">
          <Link to="/account" className="hover:text-gray-700">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">收款绑定</span>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-6 shadow-sm">
          <h1 className="text-xl font-bold text-gray-900 mb-1">绑定提现收款信息</h1>
          <p className="text-sm text-gray-500 mb-6">可绑定 U 地址或银行卡，提现时会使用已保存的信息。</p>

          {loading ? (
            <div className="py-10 text-center text-sm text-gray-400">加载中…</div>
          ) : (
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">绑定U地址</span>
                <input
                  value={form.usdtAddress}
                  onChange={(e) => updateField('usdtAddress', e.target.value)}
                  className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400"
                  placeholder="USDT 地址"
                />
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">持卡人</span>
                  <input value={form.bankAccountName} onChange={(e) => updateField('bankAccountName', e.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">银行名称</span>
                  <input value={form.bankName} onChange={(e) => updateField('bankName', e.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400" />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">银行卡号</span>
                <input value={form.bankCardNo} onChange={(e) => updateField('bankCardNo', e.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400" />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">开户地址/备注</span>
                <input value={form.bankBranch} onChange={(e) => updateField('bankBranch', e.target.value)} className="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-gray-400" />
              </label>

              {message && <div className="text-sm text-emerald-600">{message}</div>}
              {error && <div className="text-sm text-red-500">{error}</div>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-700 disabled:opacity-60"
              >
                {submitting ? '保存中…' : '保存绑定信息'}
              </button>
            </div>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
}
